/**
 * 一張票種原價表：成人（`base_price`）＋各非成人票種（`ticket_prices`）。
 *
 * 商品有**兩張**這種表——非住客與住客（見 migration 20260819050000）。做成元件是因為
 * 這裡已經出過一次漂移：業主價目表把兒童分成 3-5 / 6-11 兩段時，定價推廣頁跟上了，
 * 商品頁沒有，於是那兩個票種在 CMS 裡永遠加不進商品的價目表——而「可售票種 = adult ∪
 * 價目表的鍵」，所以它們也永遠賣不出去。兩張表各寫一份的話，下次只會再漂一次。
 */

import { NON_ADULT_TICKET_TYPES, ticketTypeLabel } from '../lib/ticket-type-label';

export interface TicketPriceTableProps {
  /** 成人原價（受控字串，允許空字串以表達「未填」）。 */
  basePrice: string;
  onBasePriceChange: (v: string) => void;
  /** 非成人票種原價；鍵存在 = 該票種可售。 */
  ticketPrices: Record<string, string>;
  onTicketPricesChange: (next: Record<string, string>) => void;
  /** 成人價是否必填（非住客表必填；住客表由「另有住客價」開關決定）。 */
  adultRequired?: boolean;
  /** 成人欄位右側的附加元素（幣別下拉等）。 */
  adultTrailing?: React.ReactNode;
  /** 全部欄位唯讀（住客價未啟用時）。 */
  disabled?: boolean;
  adultPlaceholder?: string;
  idPrefix: string;
}

export function TicketPriceTable({
  basePrice,
  onBasePriceChange,
  ticketPrices,
  onTicketPricesChange,
  adultRequired = false,
  adultTrailing,
  disabled = false,
  adultPlaceholder = '必填',
  idPrefix,
}: TicketPriceTableProps): JSX.Element {
  return (
    <>
      {/* 成人＝base_price */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ width: 110, fontSize: 14 }}>成人</span>
        <input
          id={`${idPrefix}-adult`}
          type="number"
          min={0}
          step="0.01"
          value={basePrice}
          required={adultRequired}
          disabled={disabled}
          placeholder={adultPlaceholder}
          onChange={(e) => onBasePriceChange(e.target.value)}
          style={{ width: 160, background: disabled ? '#f3f4f6' : '#fff' }}
        />
        {adultTrailing}
      </div>

      {/* 其他票種：勾選 → 可售 + 填原價 */}
      {NON_ADULT_TICKET_TYPES.map((type) => {
        const enabled = type in ticketPrices;
        return (
          <div
            key={type}
            style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}
          >
            <label
              style={{ width: 110, display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}
            >
              <input
                type="checkbox"
                checked={enabled}
                disabled={disabled}
                onChange={() => {
                  const next = { ...ticketPrices };
                  if (enabled) delete next[type];
                  else next[type] = '';
                  onTicketPricesChange(next);
                }}
              />
              {ticketTypeLabel(type)}
            </label>
            <input
              id={`${idPrefix}-${type}`}
              type="number"
              min={0}
              step="0.01"
              value={ticketPrices[type] ?? ''}
              disabled={disabled || !enabled}
              placeholder={enabled ? '原價' : '（未售）'}
              onChange={(e) => onTicketPricesChange({ ...ticketPrices, [type]: e.target.value })}
              style={{ width: 160, background: !disabled && enabled ? '#fff' : '#f3f4f6' }}
            />
          </div>
        );
      })}
    </>
  );
}
